import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, MapPin, Clock, CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * v2.7.58+E35 · DELIVERY SYSTEM · Section 4
 * Active Delivery Acceptance Panel — mounted on the driver dashboard.
 *
 *  · Polls `/api/deliveries/mine/active` every 30 s (and on GO ONLINE toggles).
 *  · Renders a pulsing amber card for every DISPATCHED manifest waiting for
 *    driver acknowledgement (Accept · Decline).
 *  · Renders a green "Continue Delivery" tile for IN_PROGRESS manifests
 *    that jumps the driver into the sequential-stop checklist page.
 */
export default function ActiveDeliveryBanner() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/deliveries/mine/active");
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      /* silent — driver may be brand-new, no assignments is normal */
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const accept = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/deliveries/mine/${id}/accept`);
      toast.success("Delivery accepted — opening stop checklist");
      nav(`/delivery/active/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept delivery");
    } finally { setBusyId(null); }
  };

  const decline = async (id) => {
    if (!window.confirm("Decline this delivery? It will be released back to dispatch.")) return;
    setBusyId(id);
    try {
      await api.post(`/deliveries/mine/${id}/decline`);
      toast.message("Delivery released back to dispatch");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decline delivery");
    } finally { setBusyId(null); }
  };

  if (!items.length) return null;

  return (
    <div className="mb-3 space-y-3" data-testid="active-delivery-banner">
      {items.map((d) => {
        const dispatched  = d.status === "DISPATCHED";
        const inProgress  = d.status === "IN_PROGRESS";
        const stopCount   = d.stop_count || (d.stops || []).length;
        const pickupNames = (d.stops || []).map(s => s.business_name).filter(Boolean);
        const finalized   = (d.stops || []).filter(s => s.picked_up_at).length;
        return (
          <Card
            key={d.id}
            data-testid={`active-delivery-card-${d.id}`}
            className={dispatched
              ? "p-4 bg-amber-500/10 border-2 border-amber-400/60 ring-2 ring-amber-400/30 animate-pulse-slow"
              : "p-4 bg-emerald-500/10 border-2 border-emerald-400/50"}
          >
            <div className="flex items-start gap-3">
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${dispatched ? "bg-amber-400 text-zinc-950" : "bg-emerald-400 text-zinc-950"}`}>
                <Package className="h-5 w-5"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded ${dispatched ? "bg-amber-400 text-zinc-950" : "bg-emerald-400 text-zinc-950"}`}>
                    {dispatched ? "NEW ASSIGNMENT" : "IN PROGRESS"}
                  </span>
                  <span className="text-[10px] text-zinc-400 font-mono">#{d.id.slice(0, 8)}</span>
                </div>
                <div className="text-lg font-extrabold text-zinc-100 mt-1">
                  {stopCount} stop{stopCount === 1 ? "" : "s"} · {d.customer_name}
                </div>
                {pickupNames.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {pickupNames.map((n, i) => (
                      <span key={i} className="text-[11px] bg-zinc-900/70 border border-zinc-700 rounded px-2 py-0.5 text-zinc-300">
                        {i + 1}. {n}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-2 text-xs text-zinc-300 flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0"/>
                  <span className="truncate">Final drop-off · {d.dropoff_address}</span>
                </div>
                {d.pickup_requested_at && (
                  <div className="mt-1 text-xs text-zinc-400 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0"/>
                    Pickup target · {new Date(d.pickup_requested_at).toLocaleString()}
                  </div>
                )}
                {typeof d.quote_total === "number" && (
                  <div className="mt-1 text-xs text-zinc-300">
                    Payout · <span className="font-bold text-emerald-300">${Number(d.quote_total).toFixed(2)}</span>
                    {typeof d.quote_tip_amount === "number" && d.quote_tip_amount > 0 && (
                      <span className="text-zinc-500"> (incl. ${d.quote_tip_amount.toFixed(2)} tip)</span>
                    )}
                  </div>
                )}
                {inProgress && (
                  <div className="mt-1 text-[11px] text-emerald-300 font-bold">
                    Progress · {finalized}/{stopCount} stops picked up
                  </div>
                )}

                <div className="mt-3 flex gap-2 flex-wrap">
                  {dispatched && (
                    <>
                      <Button
                        onClick={() => accept(d.id)}
                        disabled={busyId === d.id}
                        data-testid={`accept-delivery-${d.id}`}
                        className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold tracking-wider"
                      >
                        {busyId === d.id
                          ? <Loader2 className="h-4 w-4 animate-spin mr-1"/>
                          : <CheckCircle2 className="h-4 w-4 mr-1"/>}
                        Accept Delivery
                      </Button>
                      <Button
                        onClick={() => decline(d.id)}
                        disabled={busyId === d.id}
                        data-testid={`decline-delivery-${d.id}`}
                        variant="outline"
                        className="bg-zinc-950 border-rose-500/50 text-rose-300 hover:bg-rose-500/10"
                      >
                        <XCircle className="h-4 w-4 mr-1"/>Decline
                      </Button>
                    </>
                  )}
                  {inProgress && (
                    <Button
                      onClick={() => nav(`/delivery/active/${d.id}`)}
                      data-testid={`continue-delivery-${d.id}`}
                      className="bg-emerald-400 hover:bg-emerald-300 text-zinc-950 font-extrabold tracking-wider"
                    >
                      Continue Delivery
                      <ArrowRight className="h-4 w-4 ml-1"/>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
